import { JourneyStop, RelayPassport } from "../../relay/RelayTypes";
import { PassportMilestoneCard } from "./PassportMilestoneCard";

const COUNTRY_CENTERS: Record<string, [number, number]> = {
  "United States": [-98.5, 39.8],
  "India": [78.9, 20.6],
  "Colombia": [-74.3, 4.6],
  "Germany": [10.4, 51.2],
  "Norway": [8.5, 60.5],
  "Canada": [-106.3, 56.1],
  "United Kingdom": [-3.4, 55.4],
  "France": [2.2, 46.2],
  "Australia": [133.8, -25.3],
  "Brazil": [-51.9, -14.2],
  "Mexico": [-102.6, 23.6],
  "Japan": [138.3, 36.2],
  "South Africa": [22.9, -30.6],
  "Philippines": [121.8, 12.9],
  "Indonesia": [113.9, -0.8],
};

const coordinatesForStop = (stop: JourneyStop): [number, number] | null => {
  if (stop.location.longitude != null && stop.location.latitude != null) {
    return [stop.location.longitude, stop.location.latitude];
  }
  return COUNTRY_CENTERS[stop.location.country] || null;
};

const projectStop = (stop: JourneyStop) => {
  const [longitude, latitude] = coordinatesForStop(stop) || [0, 0];
  return {
    x: 8 + ((longitude + 180) / 360) * 84,
    y: 12 + ((90 - latitude) / 180) * 68,
  };
};

export const BananaPassport = ({ passport, showMilestone = false }: { passport: RelayPassport | null; showMilestone?: boolean }) => {
  if (!passport) return null;
  const visibleStops = passport.stops.filter((stop) => coordinatesForStop(stop) != null).slice(-12);
  const points = visibleStops.map(projectStop);
  const metrics = [
    [passport.stopCount, "stops"],
    [passport.travelMiles.toLocaleString("en-US"), "miles"],
    [passport.countryCount, "countries"],
    [passport.descendantCount, "descendants"],
  ];

  return <div className="space-y-4">
    {showMilestone && <PassportMilestoneCard passport={passport} />}
    <section className="min-w-0 overflow-hidden rounded-2xl border border-yellow-200 bg-gradient-to-br from-yellow-50 via-white to-sky-50 shadow-sm">
      <div className="p-4 sm:p-6">
        <div className="flex min-w-0 items-start justify-between gap-2 sm:gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-yellow-700 sm:text-xs sm:tracking-[0.2em]">Banana Passport</p>
            <h3 className="mt-1 text-xl font-black leading-tight text-gray-900 sm:text-2xl">
              Banana #{passport.serialNumber?.toLocaleString("en-US") || "—"}
            </h3>
            <p className="mt-1 text-sm leading-snug text-gray-600">
              You are part of generation {passport.generation + 1} of this journey.
            </p>
          </div>
          <div className="shrink-0 rounded-full bg-yellow-400 px-2.5 py-1 text-xs font-bold text-gray-900 sm:px-3 sm:text-sm">🍌 LIVE</div>
        </div>

        <div className="mt-5 grid grid-cols-2 overflow-hidden rounded-xl border border-gray-200 bg-white/85 sm:grid-cols-4">
          {metrics.map(([value, label], index) => (
            <div
              key={String(label)}
              className={`min-w-0 p-3 text-center sm:p-3.5 ${index === 0 ? "border-b border-r border-gray-200 sm:border-b-0" : ""} ${index === 1 ? "border-b border-gray-200 sm:border-b-0 sm:border-r" : ""} ${index === 2 ? "border-r border-gray-200" : ""}`}
            >
              <div className="truncate text-xl font-black leading-none text-gray-900 sm:text-2xl">{value}</div>
              <div className="mt-1 truncate text-[10px] font-semibold uppercase tracking-wide text-gray-500 sm:text-xs">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative h-40 border-t border-yellow-100 bg-sky-100/70 sm:h-44">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(#0ea5e9 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
        <svg viewBox="0 0 100 80" className="absolute inset-0 h-full w-full" role="img" aria-label="Approximate banana journey map">
          {points.slice(1).map((point, index) => {
            const previous = points[index];
            const curveY = Math.max(4, Math.min(previous.y, point.y) - 12);
            return (
              <path
                key={`route-${index}`}
                d={`M ${previous.x} ${previous.y} Q ${(previous.x + point.x) / 2} ${curveY} ${point.x} ${point.y}`}
                fill="none"
                stroke="#eab308"
                strokeWidth="1.4"
                strokeDasharray="3 2"
              />
            );
          })}
          {points.map((point, index) => (
            <g key={`stop-${index}`}>
              <circle cx={point.x} cy={point.y} r={index === points.length - 1 ? 3 : 2} fill={index === points.length - 1 ? "#facc15" : "#111827"} stroke="white" strokeWidth="1" />
              {index === points.length - 1 && <text x={point.x + 3.5} y={point.y + 1} fontSize="4">🍌</text>}
            </g>
          ))}
        </svg>
        {visibleStops.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center text-sm font-medium text-gray-600">
            This banana's next toss will begin its visual journey.
          </div>
        )}
        {visibleStops.length > 0 && (
          <div className="absolute bottom-3 left-3 right-3 grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 rounded-xl bg-white/90 px-3 py-2 text-[11px] font-semibold text-gray-700 shadow-sm backdrop-blur sm:text-xs">
            <span className="truncate">{visibleStops[0].location.label}</span>
            <span className="text-yellow-600">→</span>
            <span className="truncate text-right">{visibleStops[visibleStops.length - 1].location.label}</span>
          </div>
        )}
      </div>
      <p className="px-4 py-3 text-center text-[11px] leading-snug text-gray-500 sm:px-5">Locations are intentionally approximate to protect privacy.</p>
    </section>
  </div>;
};
